import { useState, useEffect } from "react";
import {
    Search,
    Eye,
    CheckCircle,
    XCircle,
    DollarSign,
    Loader2,
    Building2,
} from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "react-hot-toast";
import adminPaymentService from "../Services/adminPaymentService";
import paymentMethodsService from "../Services/paymentMethods";

export default function AdminPublisherPayments() {
    const [publishers, setPublishers] = useState([]);
    const [searchTerm, setSearchTerm] = useState("");
    const [selectedPublisher, setSelectedPublisher] = useState(null);
    const [isLoading, setIsLoading] = useState(true);

    useEffect(() => {
        loadData();
    }, []);

    const loadData = async () => {
        setIsLoading(true);
        try {
            const data = await adminPaymentService.getAllPublishersWithPayments();
            console.log(data);
            setPublishers(data);
        } catch {
            toast.error("Failed to load publisher data");
        } finally {
            setIsLoading(false);
        }
    };

    const handleVerify = async (publisherId, methodId) => {
        try {
            const updated = await adminPaymentService.toggleVerification(methodId);
            setPublishers((prev) =>
                prev.map((pub) =>
                    pub.id === publisherId
                        ? {
                            ...pub,
                            paymentMethods: pub.paymentMethods.map((m) =>
                                m.id === methodId ? { ...m, verified: updated.verified } : m
                            ),
                        }
                        : pub
                )
            );
            toast.success(`Payment method ${updated.verified ? "verified" : "unverified"}`);
        } catch {
            toast.error("Failed to verify method");
        }
    };

    const filtered = publishers.filter(
        (p) =>
            p.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
            p.email.toLowerCase().includes(searchTerm.toLowerCase())
    );


    return (
        <div className="max-w-7xl mx-auto p-6">
            {/* Header */}
            <div className="flex justify-between items-center mb-8">
                <motion.h2
                    initial={{ opacity: 0, y: -10 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="text-3xl font-extrabold bg-gradient-to-r from-blue-500 to-purple-500 bg-clip-text text-transparent"
                >
                    Publisher Bank Accounts
                </motion.h2>
                <div className="relative w-64">
                    <Search className="absolute left-3 top-2.5 text-gray-400" size={18} />
                    <input
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        placeholder="Search publisher..."
                        className="w-full pl-9 pr-3 py-2 rounded-lg border border-gray-300 dark:border-gray-700 dark:bg-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-blue-500"
                    />
                </div>
            </div>

            {/* Loading */}
            {isLoading ? (
                <div className="flex justify-center py-20">
                    <Loader2 className="animate-spin text-blue-500 w-10 h-10" />
                </div>
            ) : filtered.length === 0 ? (
                <p className="text-center text-gray-500 dark:text-gray-400 py-10">
                    No publishers found
                </p>
            ) : (
                <div className="space-y-6">
                    {filtered.map((publisher) => (
                        <motion.div
                            key={publisher.id}
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-5 shadow-sm"
                        >
                            {/* Header */}
                            <div className="flex justify-between items-center mb-4">
                                <div>
                                    <h3 className="text-lg font-bold text-gray-900 dark:text-white">
                                        {publisher.name}
                                    </h3>
                                    <p className="text-sm text-gray-500">{publisher.email}</p>
                                </div>
                                <button
                                    onClick={() =>
                                        setSelectedPublisher(
                                            selectedPublisher?.id === publisher.id ? null : publisher
                                        )
                                    }
                                    className="text-sm px-3 py-1 rounded-md bg-blue-50 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400 hover:bg-blue-100"
                                >
                                    {selectedPublisher?.id === publisher.id ? "Hide" : "View"}
                                </button>
                            </div>

                            {/* Payment Methods */}
                            <AnimatePresence>
                                {selectedPublisher?.id === publisher.id && (
                                    <motion.div
                                        initial={{ opacity: 0, height: 0 }}
                                        animate={{ opacity: 1, height: "auto" }}
                                        exit={{ opacity: 0, height: 0 }}
                                        transition={{ duration: 0.25 }}
                                        className="overflow-hidden"
                                    >
                                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                            {publisher.paymentMethods.length === 0 ? (
                                                <p className="text-sm text-gray-400 italic">
                                                    No payment methods registered.
                                                </p>
                                            ) : (
                                                publisher.paymentMethods.map((m) => (
                                                    <div
                                                        key={m.id}
                                                        className={`p-4 rounded-lg border ${m.verified
                                                            ? "border-green-300 bg-green-50 dark:bg-green-900/10"
                                                            : "border-gray-200 dark:border-gray-700"
                                                            }`}
                                                    >
                                                        <div className="flex justify-between items-start">
                                                            <div>
                                                                <h4 className="font-semibold text-gray-800 dark:text-gray-200 flex items-center gap-2">
                                                                    <Building2 className="text-blue-500" size={18} />
                                                                    {m.method === "Bank"
                                                                        ? "Bank / Wire Transfer"
                                                                        : m.method}
                                                                    {m.isActive && (
                                                                        <span className="text-xs bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-400 px-2 py-0.5 rounded-full ml-2">
                                                                            Active
                                                                        </span>
                                                                    )}
                                                                </h4>

                                                                {/* BANK TRANSFER DETAILS */}
                                                                {m.method === "Bank" && (
                                                                    <div className="text-sm text-gray-600 dark:text-gray-400 mt-2 space-y-1">
                                                                        <p><strong>Bank:</strong> {m.bankName || "—"}</p>
                                                                        <p><strong>Account Title:</strong> {m.accountName || "—"}</p>
                                                                        <p><strong>Account Number:</strong> {m.accountNumber || "—"}</p>
                                                                        <p><strong>SWIFT:</strong> {m.swiftCode || "—"}</p>
                                                                        <p><strong>IBAN:</strong> {m.ibanCode || "—"}</p>
                                                                        <p><strong>Branch Code:</strong> {m.branchCode || "—"}</p>
                                                                        <p><strong>Country:</strong> {m.country || "—"} | <strong>City:</strong> {m.city || "—"}</p>
                                                                        <p><strong>Postal Code:</strong> {m.postalCode || "—"}</p>
                                                                        <p><strong>ACH Routing:</strong> {m.achRoutingNo || "N/A"}</p>
                                                                        <p><strong>Fedwire No:</strong> {m.fedwireNo || "N/A"}</p>
                                                                    </div>
                                                                )}

                                                                {/* PAYPAL DETAILS */}
                                                                {m.method === "Paypal" && (
                                                                    <div className="text-sm text-gray-600 dark:text-gray-400 mt-2 space-y-1">
                                                                        <p><strong>Email:</strong> {m.paypalEmail || "—"}</p>
                                                                    </div>
                                                                )}

                                                                {/* PAYONEER DETAILS */}
                                                                {m.method === "Payoneer" && (
                                                                    <div className="text-sm text-gray-600 dark:text-gray-400 mt-2 space-y-1">
                                                                        <p><strong>Email:</strong> {m.payoneerEmail || "—"}</p>
                                                                    </div>
                                                                )}

                                                                {/* BTC DETAILS */}
                                                                {m.method === "BTC" && (
                                                                    <div className="text-sm text-gray-600 dark:text-gray-400 mt-2 space-y-1">
                                                                        <p><strong>BTC Address:</strong> {m.btcAddress || "—"}</p>
                                                                    </div>
                                                                )}

                                                                {/* UPI DETAILS */}
                                                                {m.method === "UPI" && (
                                                                    <div className="text-sm text-gray-600 dark:text-gray-400 mt-2 space-y-1">
                                                                        <p><strong>UPI ID:</strong> {m.upiId || "—"}</p>
                                                                        {m.upiName && <p><strong>Account Holder:</strong> {m.upiName}</p>}
                                                                        {m.upiMobile && <p><strong>Mobile:</strong> {m.upiMobile}</p>}
                                                                    </div>
                                                                )}

                                                            </div>

                                                            {/* ACTION BUTTONS */}
                                                            <div className="flex flex-col items-end gap-2">
                                                                <button
                                                                    onClick={() => handleVerify(publisher.id, m.id)}
                                                                    className={`p-2 rounded-full ${m.verified
                                                                        ? "text-green-500 hover:bg-green-100 dark:hover:bg-green-900/20"
                                                                        : "text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-600"
                                                                        }`}
                                                                >
                                                                    {m.verified ? <CheckCircle size={20} /> : <XCircle size={20} />}
                                                                </button>
                                                                <button
                                                                    className="text-sm text-blue-500 hover:underline flex items-center gap-1"
                                                                    onClick={async () => {
                                                                        try {
                                                                            await paymentMethodsService.SetMethodForInvoicing(m.id);
                                                                            toast.success("Payment method set as active for invoicing");
                                                                        } catch {
                                                                            toast.error("Failed to update payment method");
                                                                        }
                                                                    }}
                                                                >
                                                                    <DollarSign size={16} /> Use for Invoicing
                                                                </button>
                                                            </div>
                                                        </div>
                                                    </div>
                                                ))
                                            )}
                                        </div>
                                    </motion.div>
                                )}
                            </AnimatePresence>
                        </motion.div>
                    ))}
                </div>

            )}
        </div>
    );
}
